import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaWater, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const SnellsLawCalculator = () => {
  const [n1, setN1] = useState('');
  const [n2, setN2] = useState('');
  const [theta1, setTheta1] = useState('');
  const [theta2, setTheta2] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateRefraction = () => {
    if (!n1 || !n2 || !theta1) {
      alert('Please enter all required values');
      return;
    }

    const n1Value = parseFloat(n1);
    const n2Value = parseFloat(n2);
    const theta1Value = parseFloat(theta1);

    // Validate inputs
    if (isNaN(n1Value) || isNaN(n2Value) || isNaN(theta1Value)) {
      alert('Please enter valid numbers');
      return;
    }

    if (n1Value <= 0 || n2Value <= 0) {
      alert('Refractive indices must be positive numbers');
      return;
    }

    if (theta1Value < 0 || theta1Value > 90) {
      alert('Angle of incidence must be between 0° and 90°');
      return;
    }

    // Calculate theta2 using Snell's law: n1 * sin(theta1) = n2 * sin(theta2)
    const sinTheta2 = (n1Value * Math.sin(theta1Value * Math.PI / 180)) / n2Value;
    
    // Check for total internal reflection
    if (sinTheta2 > 1) {
      setTheta2('Total Internal Reflection');
      setShowGraph(true);
      return;
    }

    const theta2Value = Math.asin(sinTheta2) * 180 / Math.PI;
    setTheta2(theta2Value.toFixed(2));
    setShowGraph(true);
  };

  const generateGraphData = () => {
    // Check if all required values are present and valid
    if (!n1 || !n2 || !theta1 || !theta2) return null;

    const n1Value = parseFloat(n1);
    const n2Value = parseFloat(n2);
    const theta1Value = parseFloat(theta1);
    const theta2Value = theta2 === 'Total Internal Reflection' ? 90 : parseFloat(theta2);

    // Validate the values
    if (isNaN(n1Value) || isNaN(n2Value) || isNaN(theta1Value) || isNaN(theta2Value)) {
      return null;
    }

    // Generate refraction-style graph data
    const series = [
      {
        name: 'Light Path',
        data: Array.from({ length: 50 }, (_, i) => {
          const angle = (i / 50) * (theta2Value - theta1Value) + theta1Value;
          const radius = Math.abs(angle) * (1 + Math.sin(angle * Math.PI / 180) * 0.3);
          return {
            x: angle,
            y: radius
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        line: {
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
        colors: ['#00E396'],
      },
      xaxis: {
        title: {
          text: 'Angle (°)',
        },
        labels: {
          formatter: function(val) {
            return val.toFixed(1);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Path Length',
        },
        labels: {
          formatter: function(val) {
            return val.toFixed(2);
          }
        }
      },
      title: {
        text: 'Light Path Refraction',
        align: 'center',
      },
      fill: {
        colors: ['#00E396']
      },
      tooltip: {
        y: {
          formatter: function(val) {
            return val.toFixed(2);
          }
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-50 to-cyan-50">
      <Helmet>
        <title>Snell's Law Calculator | Physics Tool</title>
        <meta name="description" content="Calculate refraction angles using Snell's law. Interactive calculator with light path visualization." />
        <meta name="keywords" content="snell's law calculator, physics calculator, optics calculator, refraction calculator" />
        <meta property="og:title" content="Snell's Law Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate refraction angles using Snell's law. Interactive calculator with light path visualization." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Snell's Law Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate refraction angles using Snell's law. Interactive calculator with light path visualization." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/snells-law" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaWater />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Snell's Law Calculator</h1>
          <p className="text-xl text-gray-600">Calculate refraction angles using Snell's law</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Refractive Index 1 (n₁)</label>
                <input
                  type="number"
                  value={n1}
                  onChange={(e) => setN1(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter refractive index of medium 1"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Refractive Index 2 (n₂)</label>
                <input
                  type="number"
                  value={n2}
                  onChange={(e) => setN2(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter refractive index of medium 2"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Angle of Incidence (θ₁)</label>
                <input
                  type="number"
                  value={theta1}
                  onChange={(e) => setTheta1(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter angle of incidence (°)"
                />
              </div>
              <div>
                <button
                  onClick={calculateRefraction}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Refraction
                </button>
              </div>

              {n1 && n2 && theta1 && theta2 && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-blue-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-blue-800">Refractive Index 1 = {n1}</p>
                        <p className="text-blue-800">Refractive Index 2 = {n2}</p>
                        <p className="text-blue-800">Angle of Incidence = {theta1}°</p>
                        <p className="text-blue-800">Angle of Refraction = {theta2}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using Snell's law:
                      <br />
                      n₁ * sin(θ₁) = n₂ * sin(θ₂)
                      <br />
                      <br />
                      Where:
                      <br />
                      n₁ = Refractive Index 1 = {n1}
                      <br />
                      n₂ = Refractive Index 2 = {n2}
                      <br />
                      θ₁ = Angle of Incidence = {theta1}°
                      <br />
                      θ₂ = Angle of Refraction = {theta2}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Light Path</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows the light path through different media
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the refractive index of medium 1 (n₁)</li>
            <li>Enter the refractive index of medium 2 (n₂)</li>
            <li>Enter the angle of incidence (θ₁)</li>
            <li>Click "Calculate Refraction" to get the result</li>
            <li>View the light path visualization</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Refraction Calculation</h3>
              <p>Calculate angles using Snell's law</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>Unique light path visualization showing refraction</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for optics and physics students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Snell's Law?</h3>
              <p className="text-gray-600">
                Snell's law describes the relationship between the angles of incidence and refraction when light passes through different media.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Refractive indices are unitless</li>
                  <li>Angles should be in degrees (°)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Total Internal Reflection?</h3>
              <p className="text-gray-600">
                Total internal reflection occurs when:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Light travels from a medium with higher refractive index to a medium with lower refractive index</li>
                  <li>The angle of incidence is greater than the critical angle</li>
                  <li>All light is reflected back into the first medium instead of being refracted</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the light path visualization work?</h3>
              <p className="text-gray-600">
                The light path visualization shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>The path of light through different media</li>
                  <li>The angles of incidence and refraction</li>
                  <li>How light bends at the interface</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Snell's law is important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Lens design and manufacturing</li>
                  <li>Optical fibers (uses total internal reflection)</li>
                  <li>Microscopes and telescopes</li>
                  <li>Research and development</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Opticians design lenses</li>
                  <li>Students understand optics principles</li>
                  <li>Teachers demonstrate physics concepts</li>
                  <li>Anyone working with light refraction</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnellsLawCalculator;